import React from 'react';
import { Card, CardContent, Button, LinearProgress, IconButton } from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Legend } from "recharts";

const COLORS = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEEAD', '#D4A5A5', '#9A97F3', '#A8E6CF'];

const StatsPage = ({
    prepareChartData,
    calculateStats,
    formatDate,
    getDayTransactions,
    categories,
    selectedDate,
    setSelectedDate,
    setSelectedGoal,
    setDialogs,
    transactions,
    deleteTransaction,
    selectedWeekOffset,
    setSelectedWeekOffset
}) => {
    const { pieData, lineData } = prepareChartData();

    const handlePrevWeek = () => setSelectedWeekOffset(prev => prev - 1);
    const handleNextWeek = () => setSelectedWeekOffset(prev => prev + 1);
    const handleCurrentWeek = () => setSelectedWeekOffset(0);

    return (
        <div className="max-w-6xl mx-auto space-y-6 md:space-y-8">
            <div>
                <p className="text-xs uppercase tracking-[0.22em] text-teal-300/80">Аналитика</p>
                <h2 className="panel-title mt-2 text-3xl font-bold">Статистика</h2>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
                {/* Круговая диаграмма */}
                <Card elevation={3}>
                    <CardContent>
                        <h3 className="text-lg font-medium mb-4">Расходы по категориям</h3>
                        {pieData.length > 0 ? (
                            <ResponsiveContainer width="100%" height={300}>
                                <PieChart>
                                    <Pie
                                        data={pieData}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        startAngle={90}
                                        endAngle={450}
                                        dataKey="value"
                                        labelLine={false}
                                        label={({cx, cy, midAngle, innerRadius, outerRadius, percent}) => {
                                            const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                                            const x = cx + radius * Math.cos(-midAngle * Math.PI / 180);
                                            const y = cy + radius * Math.sin(-midAngle * Math.PI / 180);
                                            return percent > 0.05 ? (
                                                <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="middle">
                                                    {`${(percent * 100).toFixed(0)}%`}
                                                </text>
                                            ) : null;
                                        }}
                                    >
                                        {pieData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(value) => value.toLocaleString() + ' ₽'} />
                                    <Legend layout="vertical" align="right" verticalAlign="middle" />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex h-[300px] items-center justify-center rounded-lg border border-dashed border-white/12 bg-white/[0.03] text-center">
                                <div>
                                    <p className="text-lg font-semibold text-white">Пока нет расходов</p>
                                    <p className="mt-1 text-sm text-slate-400">Добавь первую трату, и диаграмма появится здесь.</p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* График динамики */}
                <Card elevation={3}>
                    <CardContent>
                        <h3 className="text-lg font-medium mb-4">Динамика финансов</h3>
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={lineData}>
                                <defs>
                                    <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#2196f3" stopOpacity={0}/>
                                    </linearGradient>
                                    <linearGradient id="colorIncome" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                                        <stop offset="95%" stopColor="#4caf50" stopOpacity={0}/>
                                    </linearGradient>
                                </defs>
                                <XAxis dataKey="date" />
                                <YAxis />
                                <CartesianGrid strokeDasharray="3 3" />
                                <Tooltip />
                                <Legend />
                                <Area
                                    type="monotone"
                                    dataKey="expenses"
                                    stroke="#2196f3"
                                    fillOpacity={1}
                                    fill="url(#colorExpenses)"
                                    name="Расходы"
                                />
                                <Area
                                    type="monotone"
                                    dataKey="income"
                                    stroke="#4caf50"
                                    fillOpacity={1}
                                    fill="url(#colorIncome)"
                                    name="Доходы"
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>

            {/* Контролы для навигации по неделям */}
            <div className="flex items-center justify-center gap-3 mb-4">
                <IconButton onClick={handlePrevWeek}>
                    <ChevronLeft />
                </IconButton>
                <Button 
                    variant="outlined" 
                    onClick={handleCurrentWeek}
                    disabled={selectedWeekOffset === 0}
                >
                    Текущая неделя
                </Button>
                <IconButton 
                    onClick={handleNextWeek}
                    disabled={selectedWeekOffset >= 0}
                >
                    <ChevronRight />
                </IconButton>
            </div>

            {/* Прогноз на месяц */}
            <Card elevation={3}>
                <CardContent>
                    <h3 className="text-lg font-medium mb-4">Прогноз на месяц</h3>
                    {(() => {
                        const stats = calculateStats();
                        return (
                            <>
                                {/* Первый ряд */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-4">
                                    <div className="metric-shell p-4 rounded-lg">
                                        <p className="text-sm text-gray-400 mb-1">Прошло дней</p>
                                        <p className="text-2xl font-semibold">{stats.daysPassed}</p>
                                    </div>
                                    <div className="metric-shell p-4 rounded-lg">
                                        <p className="text-sm text-gray-400 mb-1">Осталось дней</p>
                                        <p className="text-2xl font-semibold">{stats.daysLeft}</p>
                                    </div>
                                    <div className="metric-shell p-4 rounded-lg">
                                        <p className="text-sm text-gray-400 mb-1">Всего потрачено</p>
                                        <p className="text-2xl font-semibold">{stats.totalExpenses.toLocaleString()} ₽</p>
                                    </div>
                                </div>
                                {/* Второй ряд */}
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
                                    <div className="metric-shell p-4 rounded-lg">
                                        <p className="text-sm text-gray-400 mb-1">Средний расход в день</p>
                                        <p className="text-2xl font-semibold">{Math.round(stats.dailyAverage).toLocaleString()} ₽</p>
                                    </div>
                                    <div className="metric-shell p-4 rounded-lg">
                                        <p className="text-sm text-gray-400 mb-1">Прогноз расходов</p>
                                        <p className="text-2xl font-semibold">{Math.round(stats.projectedExpenses).toLocaleString()} ₽</p>
                                    </div>
                                    {stats.topNote && (
                                        <div className="metric-shell p-4 rounded-lg">
                                            <p className="text-sm text-gray-400 mb-1">Топ-заметка</p>
                                            <p className="text-lg font-medium mb-1">{stats.topNote.text}</p>
                                            <p className="text-xs text-gray-500">
                                                {new Date(stats.topNote.date).toLocaleDateString('ru-RU')}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </>
                        );
                    })()}
                </CardContent>
            </Card>

            {/* Календарь с тратами */}
            <Card elevation={3}>
                <CardContent>
                    <h3 className="text-lg font-medium mb-4">Календарь расходов</h3>
                    <div className="grid grid-cols-7 gap-1 sm:gap-2">
                        {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map(day => (
                            <div key={day} className="text-center py-1 text-[11px] sm:text-sm font-medium text-slate-400">
                                {day}
                            </div>
                        ))}
                        {(() => {
                            const today = new Date();
                            const year = today.getFullYear();
                            const month = today.getMonth();
                            const firstDay = new Date(year, month, 1);
                            const lastDay = new Date(year, month + 1, 0);
                            
                            let firstDayOffset = firstDay.getDay() - 1;
                            if (firstDayOffset === -1) firstDayOffset = 6;

                            const days = Array.from({ length: firstDayOffset + lastDay.getDate() }, (_, i) => {
                                if (i < firstDayOffset) return null;
                                return i - firstDayOffset + 1;
                            });

                            return days.map((day, index) => {
                                if (day === null) {
                                    return <div key={`empty-${index}`} className="p-2" />;
                                }

                                const currentDate = formatDate(year, month, day);
                                const { totalExpenses, totalIncome } = getDayTransactions(currentDate);
                                const isToday = day === today.getDate() && 
                                               month === today.getMonth() && 
                                               year === today.getFullYear();

                                return (
                                    <div
                                        key={day}
                                        onClick={() => setSelectedDate(currentDate)}
                                        className={`p-1 sm:p-2 text-center border rounded min-h-[54px] sm:min-h-[66px] flex flex-col justify-between cursor-pointer hover:bg-white/10 transition-colors
                                            ${isToday ? 'border-teal-300' : 'border-white/10'}
                                            ${(totalExpenses > 0 || totalIncome > 0) ? 'bg-white/5' : ''}`}
                                    >
                                        <span className={`${isToday ? 'text-teal-300 font-bold' : ''} text-sm sm:text-lg`}>
                                            {day}
                                        </span>
                                        {(totalExpenses > 0 || totalIncome > 0) && (
                                            <span className="amount-text text-[9px] leading-tight sm:text-xs text-slate-400">
                                                {totalExpenses > 0 && `-${totalExpenses.toLocaleString()} ₽`}
                                                {totalExpenses > 0 && totalIncome > 0 && <br />}
                                                {totalIncome > 0 && (
                                                    <span className="text-green-400">
                                                        +{totalIncome.toLocaleString()} ₽
                                                    </span>
                                                )}
                                            </span>
                                        )}
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </CardContent>
            </Card>

            {/* Блок с целями */}
            {categories.goals.length > 0 && (
                <Card elevation={3}>
                    <CardContent>
                        <h3 className="text-lg font-medium mb-4">Прогресс целей</h3>
                        <div className="grid gap-4">
                            {categories.goals.map(goal => (
                                <div key={goal.id} className="metric-shell p-4 rounded-lg">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            <h4 className="text-lg font-medium">{goal.name}</h4>
                                            <p className="text-sm text-gray-400">
                                                {goal.current.toLocaleString()} ₽ из {goal.target.toLocaleString()} ₽
                                            </p>
                                        </div>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={() => {
                                                setSelectedGoal(goal);
                                                setDialogs(prev => ({ ...prev, goalContribution: true }));
                                            }}
                                        >
                                            Пополнить
                                        </Button>
                                    </div>
                                    <LinearProgress
                                        variant="determinate"
                                        value={(goal.current / goal.target) * 100}
                                        sx={{
                                            height: 8,
                                            borderRadius: 4,
                                            backgroundColor: 'rgba(255,255,255,0.1)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: '#2196f3'
                                            }
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

export default StatsPage;
